import React, { useCallback, useState } from 'react'
import { GQL_CLIENT } from 'src/graphqlClient'
import {
  validateProjectFile,
  rejectProjectFile,
} from 'src/services/gql/project_files'
import type { FileView } from '../types/documents.types'
import { UsageFileRow } from './UsageFileRow'
import { FilePreviewModal } from './FilePreviewModal'

type Props = {
  files: FileView[]
  editable: boolean
  projectID: number
  onChange: () => void
  // When false, the per-row "Valider" / "Rejeter" affordances are
  // hidden — only the architect/collaborator decides file status. Used
  // by the salesman hub's "Autres documents" section so a salesman
  // who uploads a file cannot then validate their own upload. Defaults
  // to `true` to preserve the architect-side documents page behavior.
  canSetStatus?: boolean
  // When true, the "Statut" column (header + per-row status pill) is dropped
  // entirely. Used by the salesman synthesis "Pièces du dossier déposé" list,
  // where the file status is not relevant. Defaults to false.
  hideStatus?: boolean
}

// Shared grid between the in-table column-header row and each file row.
//   [ Fichier                     | Statut | Modifié | Actions ]
// The status column holds either the two decision buttons or the status tag
// plus its "remettre à vérifier" undo, so it needs room for a tag and a button.
export const USAGE_GRID_COLUMNS = '1fr 130px 110px 100px'
// Same grid without the "Statut" column (see `hideStatus`).
//   [ Fichier                              | Modifié | Actions ]
export const USAGE_GRID_COLUMNS_NO_STATUS = '1fr 110px 100px'

const columnHeaderStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: '#8c8c8c',
  padding: '4px 8px',
}

export const UsageFileTable: React.FC<Props> = ({
  files,
  editable,
  projectID,
  onChange,
  canSetStatus = true,
  hideStatus = false,
}) => {
  const gridColumns = hideStatus
    ? USAGE_GRID_COLUMNS_NO_STATUS
    : USAGE_GRID_COLUMNS

  // The preview modal is owned here (not per-row) so it can page through the
  // sibling files and validate/reject straight from the preview. `previewIdx`
  // is the index into `files` of the open file, or null when closed.
  const [previewIdx, setPreviewIdx] = useState<number | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  const previewFile =
    previewIdx !== null ? (files[previewIdx] ?? null) : null

  const canValidateFile = (file: FileView | null): boolean =>
    !!file &&
    canSetStatus &&
    editable &&
    file.origin === 'project' &&
    (file.status ?? 'PENDING') === 'PENDING'

  const goNext = useCallback(() => {
    setPreviewIdx((idx) =>
      idx !== null && idx < files.length - 1 ? idx + 1 : idx
    )
  }, [files.length])

  const goPrev = useCallback(() => {
    setPreviewIdx((idx) => (idx !== null && idx > 0 ? idx - 1 : idx))
  }, [])

  // After validating/rejecting, refresh the list and move on to the next file
  // so the reviewer can work through a batch without re-opening the modal. If
  // it was the last file, the modal stays on it (now showing its new status).
  const runAction = useCallback(
    async (action: typeof validateProjectFile) => {
      if (previewFile === null) return
      setActionLoading(true)
      try {
        await action(GQL_CLIENT, projectID, previewFile.id, undefined)
        onChange()
        setPreviewIdx((idx) =>
          idx !== null && idx < files.length - 1 ? idx + 1 : idx
        )
      } finally {
        setActionLoading(false)
      }
    },
    [previewFile, projectID, onChange, files.length]
  )

  const onAccept = useCallback(
    () => runAction(validateProjectFile),
    [runAction]
  )
  const onReject = useCallback(() => runAction(rejectProjectFile), [runAction])

  if (files.length === 0) {
    return (
      <div
        style={{
          padding: '8px 14px',
          color: '#bfbfbf',
          fontStyle: 'italic',
          fontSize: 12,
        }}
      >
        Aucun fichier déposé.
      </div>
    )
  }

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          alignItems: 'center',
          gap: 8,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <div style={columnHeaderStyle}>Fichier</div>
        {!hideStatus && <div style={columnHeaderStyle}>Statut</div>}
        <div style={columnHeaderStyle}>Modifié</div>
        <div style={columnHeaderStyle} />
      </div>
      {files.map((file, index) => (
        <UsageFileRow
          key={`${file.origin}-${file.id}`}
          file={file}
          editable={editable}
          projectID={projectID}
          onChange={onChange}
          isFirst={index === 0}
          canSetStatus={canSetStatus}
          hideStatus={hideStatus}
          onPreview={() => setPreviewIdx(index)}
        />
      ))}
      <FilePreviewModal
        file={previewFile}
        open={previewIdx !== null}
        onClose={() => setPreviewIdx(null)}
        onPrev={goPrev}
        onNext={goNext}
        hasPrev={previewIdx !== null && previewIdx > 0}
        hasNext={previewIdx !== null && previewIdx < files.length - 1}
        positionLabel={
          previewIdx !== null ? `${previewIdx + 1} / ${files.length}` : undefined
        }
        canValidate={canValidateFile(previewFile)}
        onAccept={onAccept}
        onReject={onReject}
        actionLoading={actionLoading}
      />
    </div>
  )
}
